import { useEffect, useReducer, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { WppButton, WppIconAdd, WppTypography } from '@wppopen/components-library-react'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import Table from 'components/table'
import { createCsApiUrlFromEndpoint, updateSort } from 'utils'
import { columnStructure } from './utils'
import useApi from 'hooks/useApi'
import {
  getPaginatedTableReducerEmptyState,
  paginatedTableReducer,
  setRowData,
  setTaskPosition,
  setTotalItemCount,
} from 'reducers/PaginatedTableReducer/PaginatedTableReducer'
import type { SortingDetails } from 'components/table/table-types'
import type { RegionsDetailData, RegionsListData, RegionsListParams } from '@contracts'
import type { SetSortByPayload } from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import styles from './RegionsTabView.module.scss'
import { DEFAULT_API_ITEM_LIMITS } from 'utils/constants'
import { regionSettingsEmptyDataMessage } from 'pages/Home/utils'
import useInitialiser from 'hooks/useInitialiser'

// for some reason if this is an import from other file, it will still add undefined to the type in column
type NonNullable<T> = T extends null | undefined ? never : T
const { REGIONS_API_ITEM_LIMITS } = DEFAULT_API_ITEM_LIMITS

const RegionsTabView = () => {
  const navigate = useNavigate()
  const [shouldAppend, setShouldAppend] = useState<boolean>(false)
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false)
  const [regionsTableReducerState, regionsTableReducerDispatch] = useReducer(
    paginatedTableReducer,
    getPaginatedTableReducerEmptyState(),
  )
  const getRegionsApiMethods = useApi<RegionsListData>(
    createCsApiUrlFromEndpoint(`regions?limit=${REGIONS_API_ITEM_LIMITS}&position=0`),
  )

  const {
    fetchData: regionsReq,
    data: regionsData,
    error: regionsReqError,
    isLoading: isRegionsReqLoading,
    url: regionsReqUrl,
    setUrl: setRegionUrl,
  } = getRegionsApiMethods

  const { isInitialising } = useInitialiser({ subscribedApis: [getRegionsApiMethods] })

  // call the regionsReq function on load page
  useEffect(() => {
    regionsReq().catch(console.error)
  }, [])

  // table functions
  const handleLoadMore = () => {
    if (!regionsData) return
    const { position } = regionsData
    if (position === undefined) return console.error('Position is missing')
    // set loading to true
    setIsTableLoading(true)
    // set the append true
    setShouldAppend(true)

    // get new position
    const newPosition = position + REGIONS_API_ITEM_LIMITS

    // send new position to reducer
    regionsTableReducerDispatch(setTaskPosition(newPosition))

    // create new url with new position
    const newUrl = new URL(regionsReqUrl)
    newUrl.searchParams.set('position', newPosition.toString())
    regionsReq(newUrl).catch(console.error)
    setRegionUrl(newUrl)
  }
  const handleSortingUpdate = (sortData: SortingDetails | undefined) => {
    setShouldAppend(false)
    setIsTableLoading(true)

    const sortDataPayload = sortData
      ? ({
          column: sortData.column,
          sort: `${sortData.sort}ending`,
        } as SetSortByPayload<NonNullable<RegionsListParams['sort_by']>>)
      : null

    const urlSorting = updateSort(regionsReqUrl, sortDataPayload)

    regionsTableReducerDispatch(setTaskPosition(0))
    setRegionUrl(urlSorting)
    regionsReq(urlSorting).catch(console.error)
  }
  const handleRowClicked = ({ data }: { data: RegionsDetailData }) => {
    navigate(`/settings/region/${data._id}`)
  }

  useEffect(() => {
    if (isRegionsReqLoading || !regionsData || regionsReqError) return
    if (
      regionsData.total_item_count === undefined ||
      regionsData.position === undefined ||
      regionsData.item_count === undefined ||
      !regionsData.regions
    ) {
      setIsTableLoading(false)
      setShouldAppend(false)
      return console.error('something within regionsData is missing')
    }

    regionsTableReducerDispatch(setRowData({ rowData: regionsData.regions, shouldAppend: shouldAppend }))
    regionsTableReducerDispatch(setTaskPosition(shouldAppend ? regionsData.position + regionsData.item_count : 0))
    regionsTableReducerDispatch(setTotalItemCount(regionsData.total_item_count))
    setIsTableLoading(false)
    setShouldAppend(false)
  }, [isRegionsReqLoading])

  if (regionsReqError && !isRegionsReqLoading && !regionsData) return <NotFound404Page />

  return (
    <>
      <div className={styles.regionsHeader}>
        <WppTypography type="xl-heading" tag="h2" data-testid="regions-title">
          Regions {regionsData && !regionsReqError && `(${regionsData?.total_item_count || 0})`}
        </WppTypography>
        <WppButton onClick={() => navigate('/create/region')} data-testid="create-region-button">
          <WppIconAdd slot="icon-start" />
          Add new region
        </WppButton>
      </div>
      <div className={styles.regionsData}>
        {isInitialising || regionsTableReducerState.itemCount ? (
          <Table
            columnDefs={columnStructure}
            loadedRows={regionsTableReducerState.itemCount}
            totalRows={regionsTableReducerState.totalItemCount}
            rowData={regionsTableReducerState.rowData}
            loading={isTableLoading}
            onLoadMore={handleLoadMore}
            onSortingUpdate={handleSortingUpdate}
            onRowClicked={handleRowClicked}
            isDrillable
            theme="tertiary"
            data-testid="regions-table"
            isInitialising={isInitialising}
          />
        ) : (
          <WppTypography type="s-body" tag="p" className={styles.textCenter} data-testid="no-regions-data">
            {regionSettingsEmptyDataMessage}
          </WppTypography>
        )}
      </div>
    </>
  )
}

export default RegionsTabView
